import type { ChangeEvent } from 'react'
import React from 'react'

import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  ButtonClean,
  Checkbox,
  CurrencyInput,
  Fieldset,
  HeadContent,
  Loading,
  SaveShortcutListener,
  Subsection,
} from '@fourthwall/components'
import { useFormik } from '@fourthwall/utils/dist/formik'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'

import { useDirectMessagesSettingsQuery } from '../../../../hooks/useDirectMessagesSettingsQuery'
import { useUpdateDirectMessagesSettingsMutation } from '../../hooks/useUpdateDirectMessagesSettingsMutation'
import { validationSchema } from './consts'
import type { FormValues } from './types'
import { FormFields } from './types'
import { getUpdateDirectMessagesPayload } from './utils'

export const Messages = () => {
  const { directMessagesSettingsQuery } = useDirectMessagesSettingsQuery()
  const { updateDirectMessagesSettingsMutation } = useUpdateDirectMessagesSettingsMutation()

  const formik = useFormik<FormValues>({
    initialValues: {
      messagesEnabled: directMessagesSettingsQuery.data?.enabled,
      tipsEnabled: !!directMessagesSettingsQuery.data?.tips,
      minTipEnabled: !!directMessagesSettingsQuery.data?.tips?.minimumAmount,
      minTipAmount: directMessagesSettingsQuery.data?.tips?.minimumAmount
        ? // eslint-disable-next-line no-unsafe-optional-chaining
          directMessagesSettingsQuery.data?.tips?.minimumAmount / 100
        : 3,
    },
    validationSchema,
    onSubmit: (values) => {
      updateDirectMessagesSettingsMutation.mutate([getUpdateDirectMessagesPayload(values)], {
        onSuccess: () => {
          directMessagesSettingsQuery.refetch()
        },
      })
    },
    enableReinitialize: true,
  })

  const handleMessagesEnableChange = (e: ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue(FormFields.messagesEnabled, e.target.checked)

    if (!formik.values.tipsEnabled) {
      formik.setFieldValue(FormFields.tipsEnabled, true)
    }
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <HeadContent title="Messages" />
      <Loading
        isLoading={
          directMessagesSettingsQuery.isFetching || updateDirectMessagesSettingsMutation.isLoading
        }
      >
        <Subsection title="Messages">
          <Fieldset>
            <Fieldset.Row>
              <Fieldset.Item>
                Chat with your members. Send individual or mass messages that include free or paid
                content.{' '}
                <ButtonClean
                  href="https://help.fourthwall.com/hc/en-us/articles/22555386644251-Direct-messages-tips"
                  label="Learn more"
                  target="_blank"
                  withUnderline
                />
              </Fieldset.Item>
            </Fieldset.Row>
            <Fieldset.Row>
              <Fieldset.Item>
                <Checkbox
                  {...formik.getFieldProps(FormFields.messagesEnabled)}
                  label="Enable messages"
                  onChange={handleMessagesEnableChange}
                />
              </Fieldset.Item>
            </Fieldset.Row>
          </Fieldset>
        </Subsection>
        {formik.values.messagesEnabled && (
          <Subsection title="Tips">
            <Fieldset>
              <Fieldset.Row>
                <Fieldset.Item>Members will be able to send you tips via messages.</Fieldset.Item>
              </Fieldset.Row>
              <Fieldset.Row>
                <Fieldset.Item>
                  <Checkbox
                    {...formik.getFieldProps(FormFields.tipsEnabled)}
                    label="Allow members to send tips"
                  />
                </Fieldset.Item>
              </Fieldset.Row>
              {formik.values.tipsEnabled && (
                <>
                  <Fieldset.Row inner>
                    <Fieldset.Item>
                      <Checkbox
                        {...formik.getFieldProps(FormFields.minTipEnabled)}
                        label="Set minimum tip requirement for members to send a message"
                      />
                    </Fieldset.Item>
                  </Fieldset.Row>
                  {formik.values.minTipEnabled && (
                    <Fieldset.Row inner>
                      <Fieldset.Item>
                        <div style={{ width: 160 }}>
                          <CurrencyInput
                            {...formik.getFieldProps(FormFields.minTipAmount)}
                            label="Minimum tip"
                          />
                        </div>
                      </Fieldset.Item>
                    </Fieldset.Row>
                  )}
                </>
              )}
            </Fieldset>
          </Subsection>
        )}
      </Loading>
      <SaveShortcutListener disabled={!formik.dirty} onSave={formik.submitForm} />
      <ConfirmBar
        isOpen={formik.dirty}
        isLoading={updateDirectMessagesSettingsMutation.isLoading}
        onConfirm={formik.submitForm}
        onCancel={formik.resetForm}
      />
      <LeavingGuard when={formik.dirty} isValid={formik.isValid} onSave={formik.submitForm} />
    </form>
  )
}
